// apps/office/src/pages/office/ClientDetail.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import pb from '../../lib/pocketbaseClient';
import { getNextConvertedJobId } from '../../lib/jobIdUtils';
import {
  ArrowLeft,
  Edit,
  Save,
  X,
  FileText,
  Calendar,
  User,
  Mail,
  Phone,
  MapPin,
  Tag,
  Plus,
  Trash2,
  Home as HomeIcon,
  PaintBucket,
  FolderOpen,
  DollarSign,
  Info,
  ShoppingCart,
  Receipt,
  Activity,
  RefreshCw,
  Pencil,
  Bell,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import CartManager from '../../components/office/CartManager';
import ChatBox from '../../components/Common/ChatBox';
import PaymentReminderDialog from '../../components/office/PaymentReminderDialog';

// Helper: status badge
const getStatusColor = (status) => {
  const colors = {
    Lead: 'bg-blue-100 text-blue-800',
    FollowUp: 'bg-yellow-100 text-yellow-800',
    Connected: 'bg-green-100 text-green-800',
    Confirm: 'bg-purple-100 text-purple-800',
    Converted: 'bg-emerald-100 text-emerald-800',
    Lost: 'bg-red-100 text-red-800',
    New: 'bg-gray-100 text-gray-800',
  };
  return colors[status] || 'bg-gray-100 text-gray-800';
};

const formatDateDisplay = (dateString) => {
  if (!dateString) return 'Not set';
  try {
    const d = new Date(dateString);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return 'Invalid date';
  }
};

const BUILDING_TYPES = [
  'Single Storied',
  'Two Storied (G+1)',
  'Three Storied (G+2)',
  'Four Storied (G+3)',
];
const DESIGN_PACKAGES = [
  'Standard Design Package',
  'Advance Design Package',
  'Premium Design Package',
];
const PROPERTY_TYPES = [
  '1 RK', '1 BHK', '2 BHK', '3 BHK', '4 BHK', '5 BHK',
  'Duplex', 'Office', 'Restaurant', 'Shop', 'Others',
];
const BUDGET_RANGES = [
  '3-5 Lakhs',
  '6-9 Lakhs',
  '10-15 Lakhs',
  '16-20 Lakhs',
  'More than 20 Lakhs',
];

const ClientDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [newNote, setNewNote] = useState('');
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('Cash');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [paymentStatus, setPaymentStatus] = useState('Completed');
  const [showCartManager, setShowCartManager] = useState(false);
  const [cartId, setCartId] = useState(null);

  // Reminder dialog state
  const [showReminderDialog, setShowReminderDialog] = useState(false);

  // State for editing payment status
  const [editingPaymentId, setEditingPaymentId] = useState(null);
  const [editingPaymentStatus, setEditingPaymentStatus] = useState('');
  const [showStatusEditDialog, setShowStatusEditDialog] = useState(false);

  // ---- fetch lead data ----
  const { data: lead, isLoading, error } = useQuery({
    queryKey: ['lead', id],
    queryFn: async () => {
      try {
        return await pb.collection('leads').getOne(id, {
          expand: 'assignedSales,assignedDesigner',
        });
      } catch (err) {
        if (err.name === 'AbortError') return null;
        throw err;
      }
    },
    enabled: !!id,
  });

  // ---- related data ----
  const { data: projects = [] } = useQuery({
    queryKey: ['leadProjects', id],
    queryFn: async () => {
      try {
        return await pb.collection('projects').getFullList({
          filter: `leadId = "${id}"`,
          sort: '-created',
        });
      } catch (err) {
        if (err.name !== 'AbortError') console.error(err);
        return [];
      }
    },
    enabled: !!id,
  });

  // ✅ FIXED: Quotations — use clientJobId (not leadId) and drop expand: items
  const { data: quotations = [] } = useQuery({
    queryKey: ['leadQuotations', id],
    queryFn: async () => {
      try {
        // Quotations — only clientJobId (leadId field does not exist here)
        const records = await pb.collection('quotations').getFullList({
          filter: `clientJobId = "${id}"`,
          sort: '-created',
          expand: 'clientJobId',
        });
        console.log('[ClientDetail] Quotations found:', records.length);
        return records;
      } catch (err) {
        if (err.name !== 'AbortError') console.error('[ClientDetail] Quotations error:', err);
        return [];
      }
    },
    enabled: !!id,
  });

  const { data: payments = [], refetch: refetchPayments } = useQuery({
    queryKey: ['leadPayments', id],
    queryFn: async () => {
      try {
        return await pb.collection('payments').getFullList({
          filter: `leadId = "${id}"`,
          sort: '-paymentDate',
        });
      } catch (err) {
        if (err.name !== 'AbortError') console.error(err);
        return [];
      }
    },
    enabled: !!id,
  });

  // -------- UPDATED CART QUERY (email/phone fallback) --------
  const { data: cartItems = [], refetch: refetchCart } = useQuery({
    queryKey: ['leadCart', id],
    queryFn: async () => {
      try {
        let carts = await pb.collection('carts').getFullList({
          filter: `user = "${id}"`,
        });
        if (carts.length === 0) {
          const leadData = queryClient.getQueryData(['lead', id]);
          if (leadData) {
            const allCarts = await pb.collection('carts').getFullList({
              filter: '',
              expand: 'user',
            });
            carts = allCarts.filter(cart => {
              const user = cart.expand?.userId;
              if (!user) return false;
              return (user.email === leadData.email) || (user.phone === leadData.phone);
            });
          }
        }
        if (carts.length === 0) {
          setCartId(null);
          return [];
        }
        const cart = carts[0];
        setCartId(cart.id);
        const items = await pb.collection('cart_items').getFullList({
          filter: `cartId = "${cart.id}"`,
        });
        return items;
      } catch (err) {
        if (err.name !== 'AbortError') console.error(err);
        return [];
      }
    },
    enabled: !!id,
  });

  const { data: notes = [], refetch: refetchNotes } = useQuery({
    queryKey: ['leadNotes', id],
    queryFn: async () => {
      try {
        return await pb.collection('client_notes').getFullList({
          filter: `leadId = "${id}"`,
          sort: '-created',
          expand: 'createdBy',
        });
      } catch (err) {
        if (err.name !== 'AbortError') console.error(err);
        return [];
      }
    },
    enabled: !!id,
  });

  // ✅ FIXED: Invoices — use clientJobId (not leadId)
  const { data: invoices = [] } = useQuery({
    queryKey: ['leadInvoices', id],
    queryFn: async () => {
      try {
        return await pb.collection('invoices').getFullList({
          filter: `leadId = "${id}"`,
          sort: '-created',
          expand: 'quotationId,leadId',
        });
      } catch (err) {
        if (err.name !== 'AbortError') console.error(err);
        return [];
      }
    },
    enabled: !!id,
  });

  const { data: salesUsers = [] } = useQuery({
    queryKey: ['salesUsers'],
    queryFn: () =>
      pb.collection('users')
        .getFullList({ filter: 'role = "sales" && status = "active"', sort: 'name' })
        .catch(() => []),
  });
  const { data: designerUsers = [] } = useQuery({
    queryKey: ['designerUsers'],
    queryFn: () =>
      pb.collection('users')
        .getFullList({ filter: 'role = "designer" && status = "active"', sort: 'name' })
        .catch(() => []),
  });

  // ---- clean submission ----
  const cleanForSubmission = (data) => {
    const cleaned = { ...data };
    if (cleaned.assignedSales === '' || cleaned.assignedSales === 'none') cleaned.assignedSales = null;
    if (cleaned.assignedDesigner === '' || cleaned.assignedDesigner === 'none') cleaned.assignedDesigner = null;
    if (cleaned.followupDate === '') cleaned.followupDate = null;
    Object.keys(cleaned).forEach((k) => {
      if (cleaned[k] === undefined) delete cleaned[k];
    });
    return cleaned;
  };

  // ---- mutations ----
  const updateMutation = useMutation({
    mutationFn: async (data) => {
      let updateData = { ...data };
      if (data.status === 'Converted' && (!lead?.jobId || !lead.jobId.startsWith('HD'))) {
        const newJobId = await getNextConvertedJobId();
        updateData.jobId = newJobId;
        toast.info(`✨ New Job ID: ${newJobId}`);
      }
      return pb.collection('leads').update(id, cleanForSubmission(updateData));
    },
    onSuccess: (updatedRecord) => {
      setEditForm((prev) => ({
        ...prev,
        status: updatedRecord.status,
        followupDate: updatedRecord.followupDate?.split('T')[0] || '',
      }));
      queryClient.setQueryData(['lead', id], updatedRecord);
      queryClient.invalidateQueries(['lead', id]);
      queryClient.invalidateQueries(['leads']);
      toast.success('✅ Client updated');
      setIsEditing(false);
    },
    onError: (err) => {
      console.error('❌ Update error:', err);
      let message = err.message || 'Update failed';
      if (err.originalError?.data?.message) {
        message = err.originalError.data.message;
      }
      if (err.status === 404) {
        message = 'Record not found. It may have been deleted.';
      }
      toast.error('❌ ' + message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => pb.collection('leads').delete(id),
    onSuccess: () => {
      toast.success('🗑️ Client deleted');
      navigate('/office/clients');
    },
    onError: (err) => {
      console.error('❌ Delete error:', err);
      toast.error('Delete failed: ' + (err.message || 'Unknown error'));
    },
  });

  const addNoteMutation = useMutation({
    mutationFn: async () => {
      return pb.collection('client_notes').create({
        leadId: id,
        note: newNote,
        createdBy: pb.authStore.record?.id,
      });
    },
    onSuccess: () => {
      toast.success('✅ Note added');
      setNewNote('');
      refetchNotes();
    },
    onError: (err) => {
      console.error('❌ Add note error:', err);
      toast.error('Failed to add note: ' + err.message);
    },
  });

  // ---- Payment record mutation ----
  const recordPaymentMutation = useMutation({
    mutationFn: async (paymentData) => {
      const paymentObj = {
        leadId: id,
        amount: parseFloat(paymentData.amount),
        paymentDate: paymentData.date || new Date().toISOString().split('T')[0],
        paymentMethod: paymentData.method || 'Cash',
        status: paymentData.status || 'Completed',
      };
      if (pb.authStore.record?.id) {
        paymentObj.recordedBy = pb.authStore.record.id;
      }
      console.log('📦 Creating payment:', paymentObj);
      return pb.collection('payments').create(paymentObj);
    },
    onSuccess: () => {
      toast.success('💰 Payment recorded');
      refetchPayments();
      queryClient.invalidateQueries(['paymentsDashboard']);
    },
    onError: (err) => {
      console.error('❌ Record payment error:', err);
      let errorMessage = 'Payment failed. ';
      if (err.message) errorMessage += err.message;
      if (err.data?.message) errorMessage += ' - ' + err.data.message;
      if (err.data?.data) {
        const fieldErrors = Object.entries(err.data.data)
          .map(([field, info]) => `${field}: ${info.message}`)
          .join('; ');
        errorMessage += ' (Fields: ' + fieldErrors + ')';
        console.error('🔍 Field errors:', err.data.data);
      }
      toast.error(errorMessage);
    },
  });

  // ---- Update payment status mutation ----
  const updatePaymentMutation = useMutation({
    mutationFn: async ({ paymentId, status }) => {
      return pb.collection('payments').update(paymentId, { status });
    },
    onSuccess: () => {
      toast.success('✅ Payment status updated');
      refetchPayments();
      queryClient.invalidateQueries(['paymentsDashboard']);
      setShowStatusEditDialog(false);
      setEditingPaymentId(null);
    },
    onError: (err) => {
      console.error('❌ Status update error:', err);
      toast.error('Failed to update status: ' + err.message);
    },
  });

  // ---- form handlers ----
  useEffect(() => {
    if (lead) {
      setEditForm({
        name: lead.name || '',
        email: lead.email || '',
        phone: lead.phone || '',
        address: lead.address || '',
        pincode: lead.pincode || '',
        serviceType: lead.serviceType || 'interior',
        status: lead.status || 'Lead',
        assignedSales: lead.assignedSales || '',
        assignedDesigner: lead.assignedDesigner || '',
        notes: lead.notes || '',
        followupDate: lead.followupDate?.split('T')[0] || '',
        plotArea: lead.plotArea || '',
        buildingType: lead.buildingType || '',
        designPackage: lead.designPackage || '',
        facingDirection: lead.facingDirection || '',
        propertyType: lead.propertyType || '',
        carpetArea: lead.carpetArea || '',
        budget: lead.budget || '',
        requirement: lead.requirement || '',
      });
    }
  }, [lead]);

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setEditForm((prev) => ({ ...prev, [name]: value === 'none' ? '' : value }));
  };

  const handleSave = () => {
    if (!id) {
      toast.error('No lead ID available for update');
      return;
    }
    updateMutation.mutate(editForm);
  };

  const handleDelete = () => {
    if (window.confirm('Delete this client? Cannot be undone.')) deleteMutation.mutate();
  };

  const handleAddNote = () => {
    if (!newNote.trim()) { toast.error('Please enter a note'); return; }
    addNoteMutation.mutate();
  };

  const handleRecordPayment = () => {
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    recordPaymentMutation.mutate({
      amount: paymentAmount,
      method: paymentMethod,
      date: paymentDate,
      status: paymentStatus,
    });
    setShowPaymentDialog(false);
    setPaymentAmount('');
    setPaymentStatus('Completed');
  };

  // ---- Open status edit dialog ----
  const openStatusEditDialog = (payment) => {
    setEditingPaymentId(payment.id);
    setEditingPaymentStatus(payment.status || 'Completed');
    setShowStatusEditDialog(true);
  };

  const handleStatusUpdate = () => {
    if (!editingPaymentId) return;
    updatePaymentMutation.mutate({
      paymentId: editingPaymentId,
      status: editingPaymentStatus,
    });
  };

  // ---- loading / error ----
  if (isLoading) return <div className="flex justify-center items-center h-64">Loading...</div>;
  if (error || !lead) {
    return (
      <div className="text-center py-10">
        <p className="text-red-500">Client not found.</p>
        <Button onClick={() => navigate('/office/clients')} className="mt-4">Back to Clients</Button>
      </div>
    );
  }

  const isBuilding = lead.serviceType === 'building';
  const isInterior = lead.serviceType === 'interior';

  return (
    <div className="space-y-6">
      {/* Header with gradient */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 p-8 shadow-xl">
        <div className="absolute top-0 right-0 -mt-16 -mr-16 w-64 h-64 bg-white/10 rounded-full blur-2xl" />
        <div className="absolute bottom-0 left-0 -mb-16 -ml-16 w-48 h-48 bg-white/10 rounded-full blur-2xl" />
        <div className="relative flex flex-wrap justify-between items-center gap-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/office/clients')} className="text-white/80 hover:text-white hover:bg-white/20 gap-2">
              <ArrowLeft className="w-5 h-5" /> Back
            </Button>
            <h1 className="text-2xl font-bold text-white">{isEditing ? '✏️ Edit Client' : lead.name}</h1>
            <Badge className={getStatusColor(lead.status)}>{lead.status}</Badge>
            {lead.jobId && <Badge variant="outline" className="bg-white/20 text-white border-white/30 font-mono">ID: {lead.jobId}</Badge>}
          </div>
          <div className="flex gap-2 flex-wrap">
            {!isEditing ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => setShowReminderDialog(true)}
                  className="bg-red-500/30 backdrop-blur-sm text-white border-red-300/50 hover:bg-red-500/40 hover:text-white gap-2"
                >
                  <Bell className="w-4 h-4" /> Send Reminder
                </Button>
                <Button variant="outline" onClick={() => setIsEditing(true)} className="bg-white/20 backdrop-blur-sm text-white border-white/30 hover:bg-white/30 hover:text-white gap-2">
                  <Edit className="w-4 h-4" /> Edit
                </Button>
                <Button variant="destructive" onClick={handleDelete} disabled={deleteMutation.isPending} className="gap-2">
                  <Trash2 className="w-4 h-4" /> Delete
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => setIsEditing(false)} className="bg-white/20 backdrop-blur-sm text-white border-white/30 hover:bg-white/30 hover:text-white gap-2">
                  <X className="w-4 h-4" /> Cancel
                </Button>
                <Button onClick={handleSave} disabled={updateMutation.isPending} className="bg-white text-indigo-600 hover:bg-indigo-50 shadow-lg gap-2">
                  <Save className="w-4 h-4" /> Save
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Tabs — mobile scrollable, desktop wraps */}
      <div className="sticky top-0 bg-white z-10 pt-2 border-b">
        <Tabs defaultValue="details" className="w-full">
          <div className="w-full">
            <TabsList className="flex flex-wrap gap-1 bg-slate-100 p-1 rounded-lg w-full">
              <TabsTrigger value="details" className="flex items-center gap-1 sm:gap-1.5 text-[11px] sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 data-[state=active]:bg-white data-[state=active]:shadow-sm flex-shrink-0">
                <Info className="w-4 h-4 shrink-0" /> Client Info
              </TabsTrigger>
              <TabsTrigger value="status-notes" className="flex items-center gap-1 sm:gap-1.5 text-[11px] sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 flex-shrink-0">
                <Activity className="w-4 h-4 shrink-0" /> Status
              </TabsTrigger>
              <TabsTrigger value="chat" className="flex items-center gap-1 sm:gap-1.5 text-[11px] sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 flex-shrink-0">
                <Activity className="w-4 h-4 shrink-0" /> Chat
              </TabsTrigger>
              <TabsTrigger value="quotations" className="flex items-center gap-1 sm:gap-1.5 text-[11px] sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 flex-shrink-0">
                <FileText className="w-4 h-4 shrink-0" /> Quotes ({quotations.length})
              </TabsTrigger>
              <TabsTrigger value="projects" className="flex items-center gap-1 sm:gap-1.5 text-[11px] sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 flex-shrink-0">
                <FolderOpen className="w-4 h-4 shrink-0" /> Projects ({projects.length})
              </TabsTrigger>
              <TabsTrigger value="cart" className="flex items-center gap-1 sm:gap-1.5 text-[11px] sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 flex-shrink-0">
                <ShoppingCart className="w-4 h-4 shrink-0" /> Cart ({cartItems.length})
              </TabsTrigger>
              <TabsTrigger value="payments" className="flex items-center gap-1 sm:gap-1.5 text-[11px] sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 flex-shrink-0">
                <DollarSign className="w-4 h-4 shrink-0" /> Payments ({payments.length})
              </TabsTrigger>
              <TabsTrigger value="invoices" className="flex items-center gap-1 sm:gap-1.5 text-[11px] sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 flex-shrink-0">
                <Receipt className="w-4 h-4 shrink-0" /> Invoices ({invoices.length})
              </TabsTrigger>
            </TabsList>
          </div>

          {/* ====== 1. CLIENT INFO ====== */}
          <TabsContent value="details" className="mt-6">
            <Card className="border-0 shadow-sm sm:shadow-xl overflow-hidden rounded-xl sm:rounded-2xl">
              <CardHeader className="bg-gradient-to-r from-indigo-50 to-purple-50 border-b border-indigo-100 p-4 sm:p-6">
                <CardTitle className="flex items-center gap-2 text-indigo-800">
                  <User className="w-5 h-5" /> Complete Client Profile
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 sm:p-6">
                {!isEditing ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-4">
                      <div className="flex items-start gap-3"><User className="w-5 h-5 text-indigo-500 mt-0.5" /><div><p className="text-xs text-slate-500 uppercase">Full Name</p><p className="font-semibold">{lead.name}</p></div></div>
                      <div className="flex items-start gap-3"><Mail className="w-5 h-5 text-indigo-500 mt-0.5" /><div><p className="text-xs text-slate-500 uppercase">Email</p><p>{lead.email || '—'}</p></div></div>
                      <div className="flex items-start gap-3"><Phone className="w-5 h-5 text-indigo-500 mt-0.5" /><div><p className="text-xs text-slate-500 uppercase">Phone</p><p>{lead.phone || '—'}</p></div></div>
                      <div className="flex items-start gap-3"><MapPin className="w-5 h-5 text-indigo-500 mt-0.5" /><div><p className="text-xs text-slate-500 uppercase">Address</p><p>{lead.address || '—'}</p>{lead.pincode && <p className="text-sm text-slate-500">{lead.pincode}</p>}</div></div>
                      {lead.followupDate && <div className="flex items-start gap-3"><Calendar className="w-5 h-5 text-indigo-500 mt-0.5" /><div><p className="text-xs text-slate-500 uppercase">Next Follow-up</p><p className="font-medium">{formatDateDisplay(lead.followupDate)}</p></div></div>}
                    </div>
                    <div className="space-y-4">
                      <div className="flex items-start gap-3"><Tag className="w-5 h-5 text-indigo-500 mt-0.5" /><div><p className="text-xs text-slate-500 uppercase">Service Type</p><p className="capitalize">{lead.serviceType === 'building' ? 'Building Plan' : 'Interior Design'}</p></div></div>
                      <div className="flex items-start gap-3"><User className="w-5 h-5 text-indigo-500 mt-0.5" /><div><p className="text-xs text-slate-500 uppercase">Assigned Sales</p><p>{lead.expand?.assignedSales?.name || lead.assignedSales || '—'}</p></div></div>
                      <div className="flex items-start gap-3"><User className="w-5 h-5 text-indigo-500 mt-0.5" /><div><p className="text-xs text-slate-500 uppercase">Assigned Designer</p><p>{lead.expand?.assignedDesigner?.name || lead.assignedDesigner || '—'}</p></div></div>
                      <div className="flex items-start gap-3"><Calendar className="w-5 h-5 text-indigo-500 mt-0.5" /><div><p className="text-xs text-slate-500 uppercase">Created On</p><p>{new Date(lead.created).toLocaleDateString('en-IN')}</p></div></div>
                    </div>
                    {isBuilding && (
                      <div className="col-span-full border-t pt-4 mt-2">
                        <h3 className="text-md font-semibold text-slate-800 mb-3 flex items-center gap-2"><HomeIcon className="w-5 h-5 text-indigo-600" /> Building Plan Details</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {lead.plotArea && <div><p className="text-xs text-slate-500">Plot Area</p><p className="font-medium">{lead.plotArea} sq.ft</p></div>}
                          {lead.buildingType && <div><p className="text-xs text-slate-500">Building Type</p><p className="font-medium">{lead.buildingType}</p></div>}
                          {lead.designPackage && <div><p className="text-xs text-slate-500">Design Package</p><p className="font-medium">{lead.designPackage}</p></div>}
                          {lead.facingDirection && <div><p className="text-xs text-slate-500">Facing Direction</p><p className="font-medium">{lead.facingDirection}</p></div>}
                          {lead.requirement && <div className="md:col-span-2"><p className="text-xs text-slate-500">Special Requirements</p><p className="font-medium whitespace-pre-wrap">{lead.requirement}</p></div>}
                        </div>
                      </div>
                    )}
                    {isInterior && (
                      <div className="col-span-full border-t pt-4 mt-2">
                        <h3 className="text-md font-semibold text-slate-800 mb-3 flex items-center gap-2"><PaintBucket className="w-5 h-5 text-indigo-600" /> Interior Design Details</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {lead.propertyType && <div><p className="text-xs text-slate-500">Property Type</p><p className="font-medium">{lead.propertyType}</p></div>}
                          {lead.carpetArea && <div><p className="text-xs text-slate-500">Carpet Area</p><p className="font-medium">{lead.carpetArea} sq.ft</p></div>}
                          {lead.budget && <div><p className="text-xs text-slate-500">Budget Range</p><p className="font-medium">{lead.budget}</p></div>}
                        </div>
                      </div>
                    )}
                    {/* ─── Selected Services (from selectedServices JSON) ─── */}
                    {(() => {
                      let services = [];
                      try {
                        if (lead.selectedServices) {
                          services = typeof lead.selectedServices === 'string'
                            ? JSON.parse(lead.selectedServices)
                            : lead.selectedServices;
                        }
                      } catch (e) {
                        console.warn('[ClientDetail] selectedServices parse failed:', e);
                      }
                      if (!Array.isArray(services) || services.length === 0) return null;

                      const grouped = {
                        'Design Package': [],
                        'General': [],
                        'Approval Drawing & Estimate': [],
                        'Other': [],
                      };
                      services.forEach((s) => {
                        const cat = s.category || 'Other';
                        if (grouped[cat]) grouped[cat].push(s);
                        else grouped['Other'].push(s);
                      });

                      const catColors = {
                        'Design Package': 'bg-violet-50 text-violet-800 border-violet-200',
                        'General': 'bg-blue-50 text-blue-800 border-blue-200',
                        'Approval Drawing & Estimate': 'bg-emerald-50 text-emerald-800 border-emerald-200',
                        'Other': 'bg-slate-50 text-slate-800 border-slate-200',
                      };

                      const total = services.reduce((sum, s) => sum + (Number(s.price) || 0), 0);

                      return (
                        <div className="col-span-full border-t pt-4 mt-2">
                          <h3 className="text-md font-semibold text-slate-800 mb-3 flex items-center gap-2">
                            <FileText className="w-5 h-5 text-indigo-600" />
                            Selected Services
                            <span className="text-xs font-normal bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">
                              {services.length} item{services.length > 1 ? 's' : ''}
                            </span>
                          </h3>

                          <div className="space-y-3">
                            {Object.entries(grouped).map(([cat, items]) => {
                              if (items.length === 0) return null;
                              return (
                                <div key={cat} className={'rounded-lg border p-3 ' + (catColors[cat] || catColors['Other'])}>
                                  <p className="text-[11px] font-bold uppercase tracking-wide mb-2 opacity-80">
                                    {cat} ({items.length})
                                  </p>
                                  <div className="space-y-1.5">
                                    {items.map((s, idx) => (
                                      <div key={idx} className="flex justify-between items-center gap-3 text-sm bg-white rounded px-2.5 py-1.5 border border-white">
                                        <span className="font-medium text-slate-800 break-words">{s.name}</span>
                                        {s.price ? (
                                          <span className="font-semibold text-amber-700 whitespace-nowrap">
                                            ₹{Number(s.price).toLocaleString('en-IN')}
                                          </span>
                                        ) : null}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              );
                            })}
                          </div>

                          {total > 0 && (
                            <div className="mt-3 flex justify-between items-center bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                              <span className="text-sm font-semibold text-amber-800">Total Estimate</span>
                              <span className="text-lg font-bold text-amber-700">
                                ₹{total.toLocaleString('en-IN')}
                              </span>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                    {lead.notes && (
                      <div className="col-span-full border-t pt-4 mt-2">
                        <p className="text-xs text-slate-500 uppercase">Additional Notes</p>
                        <p className="whitespace-pre-wrap text-slate-700 mt-1">{lead.notes}</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div className="space-y-1.5"><Label className="text-indigo-700 font-medium">Full Name *</Label><Input name="name" value={editForm.name} onChange={handleEditChange} className="border-indigo-200" /></div>
                      <div className="space-y-1.5"><Label className="text-indigo-700 font-medium">Email</Label><Input name="email" type="email" value={editForm.email} onChange={handleEditChange} className="border-indigo-200" /></div>
                      <div className="space-y-1.5"><Label className="text-indigo-700 font-medium">Phone</Label><Input name="phone" value={editForm.phone} onChange={handleEditChange} className="border-indigo-200" /></div>
                      <div className="space-y-1.5"><Label className="text-indigo-700 font-medium">Address</Label><Input name="address" value={editForm.address} onChange={handleEditChange} className="border-indigo-200" /></div>
                      <div className="space-y-1.5"><Label className="text-indigo-700 font-medium">Pincode</Label><Input name="pincode" value={editForm.pincode} onChange={handleEditChange} className="border-indigo-200" /></div>
                      <div className="space-y-1.5"><Label className="text-indigo-700 font-medium">Service Type</Label>
                        <Select value={editForm.serviceType} onValueChange={(val) => handleSelectChange('serviceType', val)}>
                          <SelectTrigger className="border-indigo-200"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="interior">🎨 Interior</SelectItem><SelectItem value="building">🏗️ Building</SelectItem></SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1.5"><Label className="text-indigo-700 font-medium">Status</Label>
                        <Select value={editForm.status} onValueChange={(val) => handleSelectChange('status', val)}>
                          <SelectTrigger className="border-indigo-200"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Lead">🔵 Lead</SelectItem><SelectItem value="FollowUp">🟡 Follow Up</SelectItem>
                            <SelectItem value="Connected">🟢 Connected</SelectItem><SelectItem value="Confirm">🟣 Confirm</SelectItem>
                            <SelectItem value="Converted">💎 Converted (Work Started)</SelectItem>
                    <SelectItem value="Submitted">✅ Submitted (Work Done)</SelectItem>
                    <SelectItem value="Lost">🔴 Lost</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1.5"><Label className="text-indigo-700 font-medium">Assign Sales</Label>
                        <Select value={editForm.assignedSales || 'none'} onValueChange={(val) => handleSelectChange('assignedSales', val)}>
                          <SelectTrigger className="border-indigo-200"><SelectValue placeholder="Select sales" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            {salesUsers.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1.5"><Label className="text-indigo-700 font-medium">Assign Designer</Label>
                        <Select value={editForm.assignedDesigner || 'none'} onValueChange={(val) => handleSelectChange('assignedDesigner', val)}>
                          <SelectTrigger className="border-indigo-200"><SelectValue placeholder="Select designer" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            {designerUsers.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1.5"><Label className="text-indigo-700 font-medium">Next Follow-up Date</Label>
                        <Input type="date" name="followupDate" value={editForm.followupDate} onChange={handleEditChange} className="border-indigo-200" />
                      </div>
                    </div>

                    {editForm.serviceType === 'building' && (
                      <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-5 border border-amber-200">
                        <h3 className="font-semibold text-amber-800 flex items-center gap-2"><HomeIcon className="w-5 h-5" /> Building Plan Details</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                          <div className="space-y-1.5"><Label className="text-amber-700">Plot Area (sq.ft)</Label><Input name="plotArea" value={editForm.plotArea} onChange={handleEditChange} className="border-amber-200" placeholder="e.g., 1200" /></div>
                          <div className="space-y-1.5"><Label className="text-amber-700">Building Type</Label>
                            <Select value={editForm.buildingType || 'none'} onValueChange={(val) => handleSelectChange('buildingType', val)}>
                              <SelectTrigger className="border-amber-200"><SelectValue /></SelectTrigger>
                              <SelectContent><SelectItem value="none">None</SelectItem>{BUILDING_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1.5"><Label className="text-amber-700">Design Package</Label>
                            <Select value={editForm.designPackage || 'none'} onValueChange={(val) => handleSelectChange('designPackage', val)}>
                              <SelectTrigger className="border-amber-200"><SelectValue /></SelectTrigger>
                              <SelectContent><SelectItem value="none">None</SelectItem>{DESIGN_PACKAGES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1.5"><Label className="text-amber-700">Facing Direction</Label><Input name="facingDirection" value={editForm.facingDirection} onChange={handleEditChange} className="border-amber-200" placeholder="e.g., East" /></div>
                          <div className="md:col-span-2 space-y-1.5"><Label className="text-amber-700">Special Requirements</Label><Textarea name="requirement" value={editForm.requirement} onChange={handleEditChange} rows={3} className="border-amber-200" placeholder="Describe any special needs..." /></div>
                        </div>
                      </div>
                    )}

                    {editForm.serviceType === 'interior' && (
                      <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-5 border border-emerald-200">
                        <h3 className="font-semibold text-emerald-800 flex items-center gap-2"><PaintBucket className="w-5 h-5" /> Interior Design Details</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                          <div className="space-y-1.5"><Label className="text-emerald-700">Property Type</Label>
                            <Select value={editForm.propertyType || 'none'} onValueChange={(val) => handleSelectChange('propertyType', val)}>
                              <SelectTrigger className="border-emerald-200"><SelectValue /></SelectTrigger>
                              <SelectContent><SelectItem value="none">None</SelectItem>{PROPERTY_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1.5"><Label className="text-emerald-700">Carpet Area (sq.ft)</Label><Input name="carpetArea" value={editForm.carpetArea} onChange={handleEditChange} className="border-emerald-200" placeholder="e.g., 900" /></div>
                          <div className="space-y-1.5"><Label className="text-emerald-700">Budget Range</Label>
                            <Select value={editForm.budget || 'none'} onValueChange={(val) => handleSelectChange('budget', val)}>
                              <SelectTrigger className="border-emerald-200"><SelectValue /></SelectTrigger>
                              <SelectContent><SelectItem value="none">None</SelectItem>{BUDGET_RANGES.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="space-y-1.5"><Label className="text-indigo-700 font-medium">Notes / Remarks</Label><Textarea name="notes" value={editForm.notes} onChange={handleEditChange} rows={4} className="border-indigo-200" placeholder="Any additional notes..." /></div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ====== 2. STATUS & NOTES ====== */}
          <TabsContent value="status-notes" className="mt-6">
            <Card className="border-0 shadow-xl">
              <CardHeader className="bg-gradient-to-r from-indigo-50 to-purple-50 border-b p-4 sm:p-6">
                <CardTitle className="flex items-center gap-2 text-indigo-800">
                  <Activity className="w-5 h-5" /> Status & Conversation Notes
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 sm:p-6 space-y-4 sm:space-y-6">
                <div className="bg-white rounded-xl p-4 border border-indigo-100 shadow-sm">
                  <h4 className="font-semibold text-indigo-700 mb-3 flex items-center gap-2">
                    <Edit className="w-4 h-4" /> Update Status & Follow-up
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-indigo-700 font-medium">Status</Label>
                      <Select
                        value={editForm.status}
                        onValueChange={(val) => handleSelectChange('status', val)}
                      >
                        <SelectTrigger className="border-indigo-200">
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Lead">🔵 Lead</SelectItem>
                          <SelectItem value="FollowUp">🟡 Follow Up</SelectItem>
                          <SelectItem value="Connected">🟢 Connected</SelectItem>
                          <SelectItem value="Confirm">🟣 Confirm</SelectItem>
                          <SelectItem value="Converted">💎 Converted (Work Started)</SelectItem>
                    <SelectItem value="Submitted">✅ Submitted (Work Done)</SelectItem>
                          <SelectItem value="Lost">🔴 Lost</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-indigo-700 font-medium">Next Follow-up Date</Label>
                      <Input
                        type="date"
                        name="followupDate"
                        value={editForm.followupDate}
                        onChange={handleEditChange}
                        className="border-indigo-200"
                      />
                    </div>
                  </div>
                  <div className="mt-3">
                    <Button
                      size="sm"
                      onClick={handleSave}
                      disabled={updateMutation.isPending}
                      className="bg-indigo-600 hover:bg-indigo-700 text-white"
                    >
                      <Save className="w-4 h-4 mr-1" /> Save Status
                    </Button>
                    <span className="text-xs text-slate-400 ml-3">(Updates the lead immediately)</span>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-slate-50 p-4 rounded-lg">
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Current Status</p>
                    <p className="font-semibold text-lg">{lead.status}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Next Follow-up Date</p>
                    <p className="font-semibold text-lg">
                      {formatDateDisplay(lead.followupDate)}
                    </p>
                  </div>
                </div>

                <div>
                  <Label className="text-indigo-700 font-medium">Add Note</Label>
                  <div className="flex gap-2 mt-1">
                    <Textarea
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      rows={2}
                      placeholder="Type a note about this client..."
                      className="flex-1 border-indigo-200"
                    />
                    <Button
                      onClick={handleAddNote}
                      disabled={addNoteMutation.isPending}
                      className="self-end bg-indigo-600 hover:bg-indigo-700"
                    >
                      Add Note
                    </Button>
                  </div>
                </div>

                <div>
                  <Label className="text-indigo-700 font-medium">Conversation History</Label>
                  <div className="space-y-3 mt-2 max-h-60 overflow-y-auto">
                    {notes.length === 0 ? (
                      <p className="text-slate-500 text-sm">No notes yet.</p>
                    ) : (
                      notes.map((note) => (
                        <div key={note.id} className="border-l-4 border-indigo-300 bg-slate-50 p-3 rounded">
                          <div className="flex justify-between text-xs text-slate-400 mb-1">
                            <span>{new Date(note.created).toLocaleString()}</span>
                            <span>{note.expand?.createdBy?.name || 'System'}</span>
                          </div>
                          <p className="text-sm">{note.note}</p>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ====== 3. CHAT ====== */}
          <TabsContent value="chat" className="mt-6">
            <Card className="border-0 shadow-xl">
              <CardHeader className="bg-gradient-to-r from-indigo-50 to-purple-50 border-b p-4 sm:p-6">
                <CardTitle className="flex items-center gap-2 text-indigo-800">
                  <Activity className="w-5 h-5" /> Client Chat
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 sm:p-6">
                <ChatBox leadId={id} height="500px" />
              </CardContent>
            </Card>
          </TabsContent>

          {/* ====== 4. QUOTATIONS (FIXED) ====== */}
          <TabsContent value="quotations" className="mt-6">
            <Card className="border-0 shadow-xl">
              <CardHeader className="bg-gradient-to-r from-indigo-50 to-purple-50 border-b flex flex-row justify-between items-center">
                <CardTitle className="flex items-center gap-2 text-indigo-800"><FileText className="w-5 h-5" /> Quotations</CardTitle>
                <Button size="sm" asChild><Link to={`/office/quotation/new/${id}`}><Plus className="w-4 h-4" /> New Quotation</Link></Button>
              </CardHeader>
              <CardContent className="p-4 sm:p-6">
                {quotations.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">No quotations yet.</p>
                ) : (
                  quotations.map(q => (
                    <div key={q.id} className="flex justify-between items-center p-4 border rounded-lg hover:bg-indigo-50 transition mb-3">
                      <div>
                        <p className="font-medium">#{q.quoteNumber || q.id.slice(-6)}</p>
                        <p className="text-sm text-slate-500">
                          Amount: ₹{(q.total || 0).toLocaleString('en-IN')} • Status: {q.status || 'Draft'}
                        </p>
                      </div>
                      <Button variant="ghost" size="sm" asChild>
                        <Link to={`/office/quotation/${q.id}`}>View →</Link>
                      </Button>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ====== 5. PROJECTS ====== */}
          <TabsContent value="projects" className="mt-6">
            <Card className="border-0 shadow-xl">
              <CardHeader className="bg-gradient-to-r from-indigo-50 to-purple-50 border-b flex flex-row justify-between items-center">
                <CardTitle className="flex items-center gap-2 text-indigo-800"><FolderOpen className="w-5 h-5" /> Projects</CardTitle>
                <Button size="sm" asChild><Link to={`/office/clients/${id}/project/new`}><Plus className="w-4 h-4" /> New Project</Link></Button>
              </CardHeader>
              <CardContent className="p-4 sm:p-6">
                {projects.length === 0 ? <p className="text-slate-500 text-center py-8">No projects yet.</p> : projects.map(p => (
                  <div key={p.id} className="flex justify-between items-center p-4 border rounded-lg hover:bg-indigo-50 transition mb-3">
                    <div>
                      <p className="font-medium">{p.name}</p>
                      <p className="text-sm text-slate-500">Status: {p.status}</p>
                    </div>
                    <Button variant="ghost" size="sm" asChild><Link to={`/office/projects/${p.id}`}>View →</Link></Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ====== 6. CART ====== */}
          <TabsContent value="cart" className="mt-6">
            <Card className="border-0 shadow-xl">
              <CardHeader className="bg-gradient-to-r from-indigo-50 to-purple-50 border-b flex flex-row justify-between items-center">
                <CardTitle className="flex items-center gap-2 text-indigo-800"><ShoppingCart className="w-5 h-5" /> Client's Cart</CardTitle>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => refetchCart()}><RefreshCw className="w-4 h-4" /> Refresh</Button>
                  {cartId && (
                    <Button size="sm" onClick={() => setShowCartManager(true)} className="bg-amber-500 hover:bg-amber-600 text-white">
                      <Plus className="w-4 h-4" /> Manage Cart
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="p-4 sm:p-6">
                {cartItems.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <p>Cart is empty.</p>
                    {!cartId && (
                      <p className="text-xs text-slate-400 mt-1">No cart found for this client.</p>
                    )}
                  </div>
                ) : (
                  cartItems.map(item => (
                    <div key={item.id} className="flex justify-between items-center p-4 border rounded-lg mb-3">
                      <div>
                        <p className="font-medium">{item.name}</p>
                        <p className="text-sm text-slate-500">Qty: {item.quantity} × ₹{item.rate}</p>
                      </div>
                      <p className="font-bold text-amber-600">₹{item.rate * item.quantity}</p>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ====== 7. PAYMENTS ====== */}
          <TabsContent value="payments" className="mt-6">
            <Card className="border-0 shadow-xl">
              <CardHeader className="bg-gradient-to-r from-indigo-50 to-purple-50 border-b flex flex-row justify-between items-center">
                <CardTitle className="flex items-center gap-2 text-indigo-800"><DollarSign className="w-5 h-5" /> Payments</CardTitle>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => setShowPaymentDialog(true)}><Plus className="w-4 h-4" /> Record Payment</Button>
                  <Button size="sm" variant="outline" onClick={() => refetchPayments()}><RefreshCw className="w-4 h-4" /></Button>
                </div>
              </CardHeader>
              <CardContent className="p-4 sm:p-6">
                {payments.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">No payments recorded.</p>
                ) : (
                  <div className="space-y-3">
                    {payments.map(p => (
                      <div key={p.id} className="flex justify-between items-center p-4 border rounded-lg hover:bg-indigo-50 transition">
                        <div>
                          <p className="font-medium">₹{p.amount?.toLocaleString()}</p>
                          <p className="text-sm text-slate-500">{p.paymentMethod} • {formatDateDisplay(p.paymentDate)}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge className={
                            p.status === 'Completed' ? 'bg-green-100 text-green-700' :
                            p.status === 'Pending' ? 'bg-yellow-100 text-yellow-700' :
                            p.status === 'Failed' ? 'bg-red-100 text-red-700' :
                            p.status === 'Refunded' ? 'bg-gray-100 text-gray-700' :
                            'bg-slate-100 text-slate-700'
                          }>
                            {p.status || 'Completed'}
                          </Badge>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openStatusEditDialog(p)}
                            title="Change Status"
                          >
                            <Pencil className="w-4 h-4 text-indigo-500" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ====== 8. INVOICES (FIXED) ====== */}
          <TabsContent value="invoices" className="mt-6">
            <Card className="border-0 shadow-xl">
              <CardHeader className="bg-gradient-to-r from-indigo-50 to-purple-50 border-b flex flex-row justify-between items-center">
                <CardTitle className="flex items-center gap-2 text-indigo-800"><Receipt className="w-5 h-5" /> Invoices</CardTitle>
                <Button size="sm" asChild><Link to="/office/invoices/new"><Plus className="w-4 h-4" /> Create Invoice</Link></Button>
              </CardHeader>
              <CardContent className="p-4 sm:p-6">
                {invoices.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">No invoices yet.</p>
                ) : (
                  invoices.map(inv => (
                    <div key={inv.id} className="flex justify-between items-center p-4 border rounded-lg hover:bg-indigo-50 transition mb-3">
                      <div>
                        <p className="font-medium">#{inv.invoiceNumber || '—'}</p>
                        <p className="text-sm text-slate-500">
                          ₹{(inv.totalAmount || 0).toLocaleString('en-IN')} • Status: {inv.field || 'Draft'}
                          {inv.expand?.quotationId?.quoteNumber && (
                            <span className="ml-2 text-xs text-indigo-500">
                              (from {inv.expand.quotationId.quoteNumber})
                            </span>
                          )}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="sm" asChild>
                          <Link to={`/office/invoices/${inv.id}`}>View →</Link>
                        </Button>
                        {(inv.field || 'Draft') === 'Draft' && (
                          <Button variant="ghost" size="sm" asChild>
                            <Link to={`/office/invoices/edit/${inv.id}`} className="text-amber-600">
                              Edit
                            </Link>
                          </Button>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* ----- Payment Dialog with Status Dropdown ----- */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="text-indigo-700">Record Payment</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Amount (₹)</Label>
              <Input
                type="number"
                step="any"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                placeholder="Enter amount"
              />
            </div>
            <div>
              <Label>Method</Label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Cash">Cash</SelectItem>
                  <SelectItem value="UPI">UPI</SelectItem>
                  <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                  <SelectItem value="Card">Card</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Date</Label>
              <Input
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
              />
            </div>
            <div>
              <Label>Status</Label>
              <Select value={paymentStatus} onValueChange={setPaymentStatus}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Pending">Pending</SelectItem>
                  <SelectItem value="Completed">Completed</SelectItem>
                  <SelectItem value="Failed">Failed</SelectItem>
                  <SelectItem value="Refunded">Refunded</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPaymentDialog(false)}>Cancel</Button>
            <Button
              onClick={handleRecordPayment}
              disabled={recordPaymentMutation.isPending}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              Record
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ----- Status Edit Dialog ----- */}
      <Dialog open={showStatusEditDialog} onOpenChange={setShowStatusEditDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-indigo-700">Update Payment Status</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>New Status</Label>
              <Select
                value={editingPaymentStatus}
                onValueChange={setEditingPaymentStatus}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Pending">Pending</SelectItem>
                  <SelectItem value="Completed">Completed</SelectItem>
                  <SelectItem value="Failed">Failed</SelectItem>
                  <SelectItem value="Refunded">Refunded</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStatusEditDialog(false)}>Cancel</Button>
            <Button
              onClick={handleStatusUpdate}
              disabled={updatePaymentMutation.isPending}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {updatePaymentMutation.isPending ? 'Updating...' : 'Update'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ----- Cart Manager Dialog ----- */}
      <Dialog open={showCartManager} onOpenChange={setShowCartManager}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-indigo-700">Manage Cart – {lead.name}</DialogTitle>
          </DialogHeader>
          {cartId && (
            <CartManager cartId={cartId} leadId={id} onClose={() => { setShowCartManager(false); refetchCart(); }} />
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCartManager(false); refetchCart(); }}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ----- Payment Reminder Dialog ----- */}
      <PaymentReminderDialog
        open={showReminderDialog}
        onOpenChange={setShowReminderDialog}
        client={lead}
        invoices={invoices}
        payments={payments}
      />
    </div>
  );
};

export default ClientDetail;
